// src/pages/reports/DrmFingerprinting.tsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  Pagination,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch
} from "@mui/material";
import {
  Sync as SyncIcon,
  CloudUpload as ImportIcon,
  GetApp as ExportIcon,
  PowerSettingsNew as ActivateIcon,
  PowerOff as DeactivateIcon,
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Fingerprint as FingerprintIcon,
  Visibility as VisibleIcon,
  VisibilityOff as CovertIcon,
  Schedule as ScheduleIcon,
  Layers as LayersIcon,
  Settings as SettingsIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import { authenticationService } from "src/services/authentication.service";
import { hasPermission } from "src/components/Permissions/hasPermission";
import dayjs from 'dayjs';
import reportDownloadService from 'src/services/reportDownload.service';

// Mock Data for fingerprinting configurations
const fingerprintData = [
  {
    id: 1,
    stbId: "STB-789XYZ",
    macId: "AB:CD:EF:12:34",
    fingerprintType: "Both",
    frequency: "Every 10 mins",
    validity: "Secure",
    layer: "Topmost",
    removable: "No",
    visibility: "Always",
    customization: "Enabled",
    forensicWatermark: "Enabled",
    globalFingerprint: "Enabled",
    lastFingerprint: "2023-04-20 14:25",
    compliance: "Compliant"
  },
  {
    id: 2,
    stbId: "STB-456ABC",
    macId: "BC:DE:FA:23:45",
    fingerprintType: "Visible",
    frequency: "Every 5 mins",
    validity: "Secure",
    layer: "Middle",
    removable: "Yes",
    visibility: "Most",
    customization: "Limited",
    forensicWatermark: "Enabled",
    globalFingerprint: "Enabled",
    lastFingerprint: "2023-04-19 10:15",
    compliance: "Compliant"
  },
  {
    id: 3,
    stbId: "STB-123DEF",
    macId: "CD:EF:12:34:56",
    fingerprintType: "Covert",
    frequency: "Every 15 mins",
    validity: "Compromised",
    layer: "Topmost",
    removable: "No",
    visibility: "Always",
    customization: "Enabled",
    forensicWatermark: "Disabled",
    globalFingerprint: "Enabled",
    lastFingerprint: "2023-04-21 09:45",
    compliance: "Non-Compliant"
  },
  {
    id: 4,
    stbId: "STB-789GHI",
    macId: "DE:FA:23:45:67",
    fingerprintType: "Both",
    frequency: "Every 10 mins",
    validity: "Secure",
    layer: "Topmost",
    removable: "No",
    visibility: "Always",
    customization: "Enabled",
    forensicWatermark: "Enabled",
    globalFingerprint: "Disabled",
    lastFingerprint: "2023-04-20 16:20",
    compliance: "Partially Compliant"
  },
  {
    id: 5,
    stbId: "STB-456JKL",
    macId: "EF:12:34:56:78",
    fingerprintType: "Visible",
    frequency: "Every 20 mins",
    validity: "Secure",
    layer: "Topmost",
    removable: "No",
    visibility: "Always",
    customization: "Enabled",
    forensicWatermark: "Enabled",
    globalFingerprint: "Enabled",
    lastFingerprint: "2023-04-21 14:10",
    compliance: "Compliant"
  },
  {
    id: 6,
    stbId: "STB-123MNO",
    macId: "12:34:56:78:90",
    fingerprintType: "Both",
    frequency: "Every 10 mins",
    validity: "Secure",
    layer: "Bottom",
    removable: "Yes",
    visibility: "Limited",
    customization: "Disabled",
    forensicWatermark: "Disabled",
    globalFingerprint: "Disabled",
    lastFingerprint: "2023-04-18 08:30",
    compliance: "Non-Compliant"
  }
];

// Define columns for the report download service
const columns = [
  { field: 'stbId', headerName: 'Set Top Box Identifier', minWidth: 120 },
  { field: 'macId', headerName: 'Media Access Control Address', minWidth: 150 },
  { field: 'fingerprintType', headerName: 'Visible and/or Covert fingerprinting', minWidth: 180 },
  { field: 'frequency', headerName: 'Fingerprinting frequency interval', minWidth: 150 },
  { field: 'validity', headerName: 'Fingerprint validity status', minWidth: 120 },
  { field: 'layer', headerName: 'Video layer position', minWidth: 120 },
  { field: 'removable', headerName: 'Can fingerprint be removed', minWidth: 120 },
  { field: 'visibility', headerName: 'Visibility in all scenarios', minWidth: 150 },
  { field: 'customization', headerName: 'Location/color customization', minWidth: 150 },
  { field: 'forensicWatermark', headerName: 'Forensic watermark capability', minWidth: 150 },
  { field: 'globalFingerprint', headerName: 'Global fingerprint enabled', minWidth: 150 },
  { field: 'lastFingerprint', headerName: 'Last fingerprint application', minWidth: 150 },
  { field: 'compliance', headerName: 'Regulatory compliance status', minWidth: 150 },
];

const DrmFingerprinting = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [fingerprintSettings, setFingerprintSettings] = useState({
    visible: true,
    covert: true,
    frequency: 10, // minutes
    topLayer: true,
    removable: false,
    allScenarios: true,
    randomLocation: true,
    characterLength: 12,
    globalEnabled: true,
    individualEnabled: true,
    forensicEnabled: true,
    scheduleReporting: true
  });

  const rowsPerPage = 5;
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  // Filter data based on search term
  const filteredData = fingerprintData.filter((row) =>
    Object.values(row).some(
      (val) => val.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Pagination
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Table headers
  const headers = [
    { id: 'stbId', display: 'STB ID', full: 'Set Top Box Identifier' },
    { id: 'macId', display: 'MAC ID', full: 'Media Access Control Address' },
    { id: 'fingerprintType', display: 'Type', full: 'Visible and/or Covert fingerprinting' },
    { id: 'frequency', display: 'Frequency', full: 'Fingerprinting frequency interval' },
    { id: 'validity', display: 'Validity', full: 'Fingerprint validity status' },
    { id: 'layer', display: 'Layer', full: 'Video layer position' },
    { id: 'removable', display: 'Removable', full: 'Can fingerprint be removed' },
    { id: 'visibility', display: 'Visibility', full: 'Visibility in all scenarios' },
    { id: 'customization', display: 'Customization', full: 'Location/color customization' },
    { id: 'forensicWatermark', display: 'Forensic', full: 'Forensic watermark capability' },
    { id: 'globalFingerprint', display: 'Global', full: 'Global fingerprint enabled' },
    { id: 'lastFingerprint', display: 'Last Applied', full: 'Last fingerprint application' },
    { id: 'compliance', display: 'Compliance', full: 'Regulatory compliance status' },
  ];

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const complianceStats = {
        compliant: filteredData.filter(row => row.compliance === 'Compliant').length,
        nonCompliant: filteredData.filter(row => row.compliance === 'Non-Compliant').length,
        partial: filteredData.filter(row => row.compliance === 'Partially Compliant').length,
      };

      // Generate custom filename
      const customFileName = `DRM_Fingerprinting_Report_${dayjs().format('YYYY-MM-DD_HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const success = await reportDownloadService.downloadReport({
        title: 'DRM Fingerprinting Configuration Report',
        columns,
        rows: filteredData,
        fileName: customFileName,
      
        // Excel specific configuration - using full headers
        excelHeaders: headers.map(header => header.full),
        excelRowMapper: (row: any) => [
          row.stbId || '',
          row.macId || '',
          row.fingerprintType || '',
          row.frequency || '',
          row.validity || '',
          row.layer || '',
          row.removable || '',
          row.visibility || '',
          row.customization || '',
          row.forensicWatermark || '',
          row.globalFingerprint || '',
          row.lastFingerprint || '',
          row.compliance || ''
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  const handleSettingChange = (setting) => (event) => {
    setFingerprintSettings({
      ...fingerprintSettings,
      [setting]: event.target.checked
    });
  };

  const handleNumericChange = (setting) => (event) => {
    setFingerprintSettings({
      ...fingerprintSettings,
      [setting]: parseInt(event.target.value) || 0
    });
  };

  return (
    <Box sx={{ margin: "20px 30px 0px 20px", overflow: "hidden" }}>
      {/* Header Section */}
      <Box sx={{ margin: "0px 0px 20px 0px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">
          <FingerprintIcon fontSize="large" sx={{ verticalAlign: 'middle', mr: 1 }} />
          DRM Fingerprinting Configuration
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {hasPermission(["OWNER"]) && (
            <>
              <Button 
                variant="contained" 
                startIcon={<ImportIcon />}
                sx={{ textTransform: 'none' }}
              >
                Import Config
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<ExportIcon />}
                onClick={() => handleDownload('excel')}
                disabled={downloading.excel}
                sx={{ textTransform: 'none' }}
              >
                {downloading.excel ? 'Generating...' : 'Export Excel'}
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload('pdf')}
                disabled={downloading.pdf}
                sx={{ textTransform: 'none' }}
              >
                {downloading.pdf ? 'Generating...' : 'Export PDF'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Configuration Accordion */}
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            Fingerprinting System Settings
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 3,
            p: 2
          }}>
            {/* Fingerprint Type */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <FingerprintIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Fingerprint Types
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.visible}
                    onChange={handleSettingChange('visible')}
                    color="primary"
                  />
                }
                label="Visible Fingerprinting"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.covert}
                    onChange={handleSettingChange('covert')}
                    color="primary"
                  />
                }
                label="Covert Fingerprinting"
              />
            </Box>

            {/* Frequency Settings */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <ScheduleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Frequency Settings
              </Typography>
              <TextField
                label="Interval (minutes)"
                type="number"
                value={fingerprintSettings.frequency}
                onChange={handleNumericChange('frequency')}
                size="small"
                sx={{ width: '120px', mr: 2 }}
                inputProps={{ min: 1, max: 60 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.scheduleReporting}
                    onChange={handleSettingChange('scheduleReporting')}
                    color="primary"
                  />
                }
                label="Schedule Reporting"
              />
            </Box>

            {/* Display Settings */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <LayersIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Display Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.topLayer}
                    onChange={handleSettingChange('topLayer')}
                    color="primary"
                  />
                }
                label="Topmost Layer"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.allScenarios}
                    onChange={handleSettingChange('allScenarios')}
                    color="primary"
                  />
                }
                label="All Scenarios"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.randomLocation}
                    onChange={handleSettingChange('randomLocation')}
                    color="primary"
                  />
                }
                label="Random Location"
              />
            </Box>

            {/* Security Settings */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <VerifiedIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Security Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!fingerprintSettings.removable}
                    onChange={(e) => handleSettingChange('removable')({ target: { checked: !e.target.checked } })}
                    color="primary"
                  />
                }
                label="Non-Removable"
              />
              <TextField
                label="Min Characters"
                type="number"
                value={fingerprintSettings.characterLength}
                onChange={handleNumericChange('characterLength')}
                size="small"
                sx={{ width: '120px', mt: 1 }}
                inputProps={{ min: 6, max: 24 }}
              />
            </Box>

            {/* Watermark Settings */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <VisibleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Watermark Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.globalEnabled}
                    onChange={handleSettingChange('globalEnabled')}
                    color="primary"
                  />
                }
                label="Global Fingerprint"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.individualEnabled}
                    onChange={handleSettingChange('individualEnabled')}
                    color="primary"
                  />
                }
                label="Individual Fingerprint"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={fingerprintSettings.forensicEnabled}
                    onChange={handleSettingChange('forensicEnabled')}
                    color="primary"
                  />
                }
                label="Forensic Watermark"
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<SyncIcon />}
                sx={{ mb: 2 }}
              >
                Apply Settings
              </Button>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<FingerprintIcon />}
              >
                Run Fingerprint Now
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Controls Section */}
      <Box sx={{ 
        display: "flex", 
        flexDirection: { xs: "column", md: "row" }, 
        gap: 2, 
        mb: 3,
        alignItems: { xs: "flex-start", md: "center" }
      }}>
        {/* Search Box */}
        <TextField
          size="small"
          placeholder="Search STBs..."
          InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          sx={{ width: { xs: "100%", md: "300px" } }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Compliance Filter */}
        <Box sx={{ 
          display: "flex", 
          gap: 1, 
          backgroundColor: "action.hover",
          p: 1,
          borderRadius: 1
        }}>
          <Typography variant="body1" sx={{ alignSelf: "center", mr: 1 }}>
            Compliance:
          </Typography>
          <Chip label="All" color="default" clickable />
          <Chip label="Compliant" color="success" clickable />
          <Chip label="Non-Compliant" color="error" clickable />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
          <Tooltip title="Run Fingerprint on Selected">
            <IconButton color="primary">
              <FingerprintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Generate Compliance Report">
            <IconButton color="secondary">
              <VerifiedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "auto" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 450px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <Tooltip key={header.id} title={header.full} arrow>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: '120px',
                      maxWidth: '150px'
                    }}>
                      {header.display}
                    </TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: "primary.main" }}>S</Avatar>
                      <Typography variant="body2">{row.stbId}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.macId}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {row.fingerprintType.includes('Both') ? (
                        <>
                          <Chip icon={<VisibleIcon />} label="Visible" size="small" />
                          <Chip icon={<CovertIcon />} label="Covert" size="small" />
                        </>
                      ) : (
                        <Chip 
                          icon={row.fingerprintType.includes('Visible') ? <VisibleIcon /> : <CovertIcon />} 
                          label={row.fingerprintType} 
                          size="small" 
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.frequency}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.validity} 
                      size="small" 
                      color={row.validity === "Secure" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>{row.layer}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.removable} 
                      size="small" 
                      color={row.removable === "No" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.visibility} 
                      size="small" 
                      color={row.visibility === "Always" ? "success" : "warning"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.customization} 
                      size="small" 
                      color={row.customization === "Enabled" ? "success" : "warning"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.forensicWatermark} 
                      size="small" 
                      color={row.forensicWatermark === "Enabled" ? "success" : "warning"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.globalFingerprint} 
                      size="small" 
                      color={row.globalFingerprint === "Enabled" ? "success" : "warning"} 
                    />
                  </TableCell>
                  <TableCell>{row.lastFingerprint}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {row.compliance === "Compliant" ? (
                        <VerifiedIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2">{row.compliance}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      </Paper>

      {/* Compliance Notes */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          <WarningIcon color="warning" sx={{ verticalAlign: 'middle', mr: 1 }} />
          Regulatory Compliance Notes
        </Typography>
        <Typography variant="body2" paragraph>
          • The DRM shall support and enable forensic watermarking at STB/unique consumer subscription level.
        </Typography>
        <Typography variant="body2" paragraph>
          • The fingerprinting should appear on the screens in all scenarios (menu, EPG, settings, blank screen, games).
        </Typography>
        <Typography variant="body2" paragraph>
          • The fingerprinting should be on the topmost layer of the video and should not be removable by any device/software.
        </Typography>
        <Typography variant="body2" paragraph>
          • The location, font color and background color of fingerprint should be changeable from head end.
        </Typography>
        <Typography variant="body2" paragraph>
          • The DRM shall have the capability to run fingerprinting with at least one fingerprinting every ten (10) minutes.
        </Typography>
      </Box>
    </Box>
  );
};

export default DrmFingerprinting;